import {match} from 'ts-pattern';
import {z} from 'zod';

import type {FeatureWriteRequestBody} from '@dms/api/new-vehicle';
import {FEATURE_CATEGORIES} from '@dms/features/new-vehicle';

import {optionalMoneyInput, priceInput} from './moneyInput';

export const equipmentDraftSchema = z.object({
  name: z.string().trim().min(1),
  equipmentType: z.enum(['factory-option', 'dealer-accessory', 'wheel']),
  standaloneOrderable: z.boolean(),
  // '' is the unpicked form state; submitting requires a real group.
  category: z
    .union([z.enum(FEATURE_CATEGORIES), z.literal('')])
    .refine((value) => value.length > 0),
  factoryCode: z.string(),
  defaultPriceNet: priceInput,
  costNet: optionalMoneyInput,
  scope: z.object({makeId: z.string(), modelId: z.string(), modelYearId: z.string()}).optional(),
  trimIds: z.array(z.string()),
  inclusion: z.enum(['STANDARD', 'OPTIONAL']),
});
export type EquipmentDraft = z.infer<typeof equipmentDraftSchema>;

export const emptyEquipmentDraft: EquipmentDraft = {
  name: '',
  equipmentType: 'factory-option',
  standaloneOrderable: true,
  category: '',
  factoryCode: '',
  defaultPriceNet: '0.00',
  costNet: '',
  trimIds: [],
  inclusion: 'OPTIONAL',
};

// Tenant library identity is deliberately separate from the trim attachment projection.
export function equipmentWriteBody(
  values: EquipmentDraft,
  canViewCost: boolean
): FeatureWriteRequestBody {
  const taxonomy = match(values.equipmentType)
    .with('factory-option', () => ({kind: 'option', fitStage: 'factory'}) as const)
    .with('dealer-accessory', () => ({kind: 'accessory', fitStage: 'dealer'}) as const)
    .with('wheel', () => ({kind: 'wheel', fitStage: 'factory'}) as const)
    .exhaustive();
  // BE rejects a wheel set outside wheels_tyres (DMS-NV-CT-0038).
  const category = taxonomy.kind === 'wheel' ? 'wheels_tyres' : values.category;
  if (category === '') {
    throw new Error('Equipment category must be validated before writing.');
  }
  return {
    ...taxonomy,
    standaloneOrderable: values.standaloneOrderable,
    name: values.name,
    codes: values.factoryCode ? [values.factoryCode] : [],
    category,
    factoryCode: values.factoryCode || null,
    // C1 requires a decimal even for package-only items. This is not a standalone offer price.
    defaultPriceNet: values.standaloneOrderable ? values.defaultPriceNet : '0.00',
    ...(canViewCost ? {costNet: values.costNet || null} : {}),
    version: 1,
  };
}
